import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Composer task dots (founder 07-16): after its ~5s linger the inline receipt
 * DISSOLVES into a small status dot on the composer's top rim — one dot per
 * composer-born task. This store is the session-local ledger of those tasks;
 * ComposerTaskDots renders it and derives each dot's live status from the
 * task store + the interactions feed (this store never caches status).
 */
export interface ComposerDotEntry {
  taskId: string;
  title: string;
  /** What runs the task (from the receipt) — shown in the hover panel. */
  model?: string;
  addedAt: number;
}

interface ComposerDotsState {
  entries: ComposerDotEntry[];
  /** Idempotent by taskId — a re-fired receipt never doubles a dot. */
  add: (e: { taskId: string; title: string; model?: string }) => void;
  remove: (taskId: string) => void;
}

/** Entries older than this are pruned at rehydrate — a dot from days ago is
 *  archaeology, not presence. */
const MAX_ENTRY_AGE_MS = 48 * 60 * 60 * 1000;

export const useComposerDotsStore = create<ComposerDotsState>()(
  persist(
    (set) => ({
      entries: [],
      add: (e) =>
        set((s) =>
          s.entries.some((x) => x.taskId === e.taskId)
            ? s
            : { entries: [...s.entries, { ...e, addedAt: Date.now() }] }
        ),
      remove: (taskId) =>
        set((s) => ({ entries: s.entries.filter((x) => x.taskId !== taskId) })),
    }),
    {
      // Founder incident 07-16: Cmd+R wiped the dots — "everything is lost".
      // The strip is a ledger of live work; a reload must not amnesia it.
      name: "vestra:composer-dots",
      storage: createJSONStorage(() => localStorage),
      merge: (persisted, current) => {
        const p = persisted as Partial<ComposerDotsState> | undefined;
        const cutoff = Date.now() - MAX_ENTRY_AGE_MS;
        return {
          ...current,
          entries: (p?.entries ?? []).filter((e) => e.addedAt > cutoff),
        };
      },
    }
  )
);
